import { useCallback, useMemo } from 'react';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';

export enum ValidationState {
  VALID = 'valid',
  INVALID = 'invalid',
}

export enum ValidationRuleId {
  MIN_LENGTH = 'minLength',
  HAS_LETTER = 'hasLetter',
  HAS_NUMBER = 'hasNumber',
  HAS_SPECIAL_CHAR = 'hasSpecialChar',
}

export enum TranslationKey {
  MIN_LENGTH = 'password_characters',
  HAS_LETTER = 'password_letters',
  HAS_NUMBER = 'password_number',
  HAS_SPECIAL_CHAR = 'password_special_character',
}

interface ValidationRule {
  label: string;
  regex: RegExp;
}

export interface ValidationResult {
  rule: ValidationRuleId;
  label: string;
  isValid: boolean;
  shouldShowError: boolean; // Controls visibility of error state
}

export interface PasswordValidationResult {
  validatePassword: (password: string) => boolean;
  validationResults: ValidationResult[];
}

/**
 * Hook for validating password against predefined rules
 */
export const usePasswordChecklist = (
  value: string,
  showError: boolean,
): PasswordValidationResult => {
  const { translation } = useTranslationContext();

  const validationRules = useMemo<Record<ValidationRuleId, ValidationRule>>(
    () => ({
      [ValidationRuleId.MIN_LENGTH]: {
        label: translation(TranslationKey.MIN_LENGTH),
        regex: /.{8,}/,
      },
      [ValidationRuleId.HAS_LETTER]: {
        label: translation(TranslationKey.HAS_LETTER),
        regex: /[a-zA-Z]/,
      },
      [ValidationRuleId.HAS_NUMBER]: {
        label: translation(TranslationKey.HAS_NUMBER),
        regex: /\d/,
      },
      [ValidationRuleId.HAS_SPECIAL_CHAR]: {
        label: translation(TranslationKey.HAS_SPECIAL_CHAR),
        regex: /[!@#$%^&*(),.?":{}|<>\-_+=/:;'~\\]/,
      },
    }),
    [translation],
  );

  const passwordValidationResults = useMemo<ValidationResult[]>(() => {
    const validationResults = Object.entries(validationRules).map(([ruleId, { regex, label }]) => {
      const isValid = regex.test(value);
      return {
        rule: ruleId as ValidationRuleId,
        label,
        isValid,
        shouldShowError: !isValid && showError,
      };
    });

    return validationResults;
  }, [value, showError, validationRules]);

  const validatePassword = useCallback(
    (password: string): boolean =>
      Object.values(validationRules).every(({ regex }) => regex.test(password)),
    [validationRules],
  );

  return {
    validationResults: passwordValidationResults,
    validatePassword,
  };
};
